import {
  Card,
  CardContent,
  GradientText,
  Typography,
} from '@growdash/design-system/atoms';
import { LoadingWithTextLoop } from '@growdash/design-system/molecules/index.js';
import { useQuery } from '@tanstack/react-query';

import { useGrowintel } from '@growdash/growintel/hooks/useGrowIntel';
import useTabState from '@growdash/growintel/hooks/useTabState';
import { executeFacadeRequest } from '@growdash/growintel/lib/aisha';
// @ts-ignore
import AishaCard from '@growdash/growintel/components/atoms/aisha-card';
import { useState } from 'react';
import Markdown from 'react-markdown';

type Props = {};

type MenuReviewSection = {
  'section-title': string;
  text: string;
};

type MenuReviewData = {
  sections: MenuReviewSection[];
};

export default function MenuReviewView({}: Props) {
  const { lib } = useGrowintel();
  const { filters } = useTabState('ai');
  const { aishaService } = lib;
  const [isError, setIsError] = useState(false);
  const brand = filters.menuReviewBrand;

  const { data, isFetching } = useQuery({
    queryKey: ['menu-review', brand?.id],
    queryFn: async (): Promise<MenuReviewData | undefined> => {
      if (!brand?.id || !aishaService) return undefined;
      try {
        const response: any = await executeFacadeRequest(
          'int-rev-breakfast',
          aishaService,
          { brandId: brand?.id, brand_name: brand?.brand_name },
        );
        return response.result;
      } catch (error) {
        setIsError(true);
        console.error('error during menu review', error);
        return undefined;
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isError && !isFetching) {
    return (
      <Card className="py-12 text-center text-muted-foreground">
        <p className="text-red-400">
          An error occurred while reviewing the menu. Please try again.
        </p>
      </Card>
    );
  }

  if (!data && !isFetching) {
    return (
      <Card className="py-12 text-center text-muted-foreground">
        <p>Select a brand to see menu review from AI</p>
      </Card>
    );
  }

  return (
    <>
      {isFetching && (
        <div className="z-50">
          <LoadingWithTextLoop
            loopTexts={['Analyzing your menu.', ' This might take a while.']}
          />
        </div>
      )}

      {!isFetching && data?.sections && (
        <div className="animate-fade-in">
          <AishaCard title="Menu Review" className="mt-4">
            <CardContent>
              {data.sections.map(
                (section: MenuReviewSection, index: number) => (
                  <div key={index} className="mb-6">
                    <GradientText
                      className="text-base text-left !mx-0 !justify-start mb-[10px]"
                      animationSpeed={6}
                      colors={['#5BE0DC', '#060081']}
                    >
                      <Typography className="text-gradient">
                        {section['section-title']}
                      </Typography>
                    </GradientText>

                    <Markdown>{section.text}</Markdown>
                  </div>
                ),
              )}
            </CardContent>
          </AishaCard>
        </div>
      )}
    </>
  );
}
